import React, { useEffect, useState } from "react";
import { Alert, Button, Form, Modal, Spinner } from "react-bootstrap";
import { Trash2, User } from "react-feather";
import { usePost, usePut, useDelete } from "../../api";
import { ITesterProfile, TESTER_ROLES, TesterRole } from "../../interfaces";

/** Add / edit one saved device profile. A tester can save several (e.g. a
 *  personal phone + a shared test tablet) and pick which one applies when
 *  recording a given APK test — set once per device, then reused rather than
 *  re-entered every time. */
export const TesterProfileModal: React.FC<{
  show: boolean;
  /** Null when adding a new device profile; pass the profile to edit it. */
  profile: ITesterProfile | null;
  onClose: () => void;
  onSaved: (profile: ITesterProfile) => void;
  onDeleted?: (profileId: string) => void;
}> = ({ show, profile, onClose, onSaved, onDeleted }) => {
  const { execute: createProfile, loading: creating } = usePost<{
    profile: ITesterProfile;
  }>();
  const { execute: updateProfile, loading: updating } = usePut<{
    profile: ITesterProfile;
  }>();
  const { execute: deleteProfile, loading: deleting } = useDelete<{
    success: boolean;
  }>();

  const [label, setLabel] = useState("");
  const [role, setRole] = useState<TesterRole>(TESTER_ROLES[0].value);
  const [device, setDevice] = useState("");
  const [osVersion, setOsVersion] = useState("");
  const [screenSize, setScreenSize] = useState("");
  const [appBuildVersion, setAppBuildVersion] = useState("");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!show) return;
    setLabel(profile?.label ?? "");
    setRole(profile?.role ?? TESTER_ROLES[0].value);
    setDevice(profile?.device ?? "");
    setOsVersion(profile?.osVersion ?? "");
    setScreenSize(profile?.screenSize ?? "");
    setAppBuildVersion(profile?.appBuildVersion ?? "");
    setError(null);
  }, [show, profile]);

  const saving = creating || updating;

  const handleSave = async () => {
    setError(null);
    const body = {
      label: label.trim(),
      role,
      device: device.trim(),
      osVersion: osVersion.trim(),
      screenSize: screenSize.trim(),
      appBuildVersion: appBuildVersion.trim(),
    };
    try {
      const result = profile
        ? await updateProfile(`tester-profile/${profile._id}`, body)
        : await createProfile("tester-profile", body);
      onSaved(result.profile);
      onClose();
    } catch (e: any) {
      setError(e?.data?.error ?? e?.message ?? "Failed to save profile.");
    }
  };

  const handleDelete = async () => {
    if (!profile) return;
    setError(null);
    try {
      await deleteProfile(`tester-profile/${profile._id}`);
      onDeleted?.(profile._id);
      onClose();
    } catch (e: any) {
      setError(e?.data?.error ?? e?.message ?? "Failed to delete profile.");
    }
  };

  return (
    <Modal show={show} onHide={onClose} centered>
      <Modal.Header closeButton>
        <Modal.Title className="fs-6">
          <User size={15} className="me-2" />
          {profile ? "Edit device profile" : "Add device profile"}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className="small text-muted mb-3">
          Saved once per device so test history shows what the result was
          actually tested on. Save a separate profile for each device you test
          with.
        </p>
        <Form.Group className="mb-3">
          <Form.Label className="small mb-1">Device label</Form.Label>
          <Form.Control
            size="sm"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder='e.g. "Personal Galaxy S23"'
          />
        </Form.Group>
        <Form.Group className="mb-3">
          <Form.Label className="small mb-1">Role</Form.Label>
          <Form.Select
            size="sm"
            value={role}
            onChange={(e) => setRole(e.target.value as TesterRole)}
          >
            {TESTER_ROLES.map((r) => (
              <option key={r.value} value={r.value}>
                {r.label}
              </option>
            ))}
          </Form.Select>
        </Form.Group>
        <div className="d-flex gap-3 mb-3">
          <Form.Group className="flex-fill">
            <Form.Label className="small mb-1">Device</Form.Label>
            <Form.Control
              size="sm"
              value={device}
              onChange={(e) => setDevice(e.target.value)}
              placeholder="e.g. Samsung Galaxy S23"
            />
          </Form.Group>
          <Form.Group className="flex-fill">
            <Form.Label className="small mb-1">OS &amp; version</Form.Label>
            <Form.Control
              size="sm"
              value={osVersion}
              onChange={(e) => setOsVersion(e.target.value)}
              placeholder="e.g. Android 14"
            />
          </Form.Group>
        </div>
        <div className="d-flex gap-3 mb-3">
          <Form.Group className="flex-fill">
            <Form.Label className="small mb-1">Screen size</Form.Label>
            <Form.Control
              size="sm"
              value={screenSize}
              onChange={(e) => setScreenSize(e.target.value)}
              placeholder='e.g. 6.1"'
            />
          </Form.Group>
          <Form.Group className="flex-fill">
            <Form.Label className="small mb-1">App build / version</Form.Label>
            <Form.Control
              size="sm"
              value={appBuildVersion}
              onChange={(e) => setAppBuildVersion(e.target.value)}
              placeholder="e.g. v4.2.1 (build 118)"
            />
          </Form.Group>
        </div>
        {error && (
          <Alert variant="danger" className="small py-2">
            {error}
          </Alert>
        )}
      </Modal.Body>
      <Modal.Footer className="py-2 d-flex justify-content-between">
        <div>
          {profile && (
            <Button
              size="sm"
              variant="outline-danger"
              disabled={deleting || saving}
              onClick={handleDelete}
            >
              <Trash2 size={13} className="me-1" />
              Delete
            </Button>
          )}
        </div>
        <div className="d-flex gap-2">
          <Button size="sm" variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            size="sm"
            variant="primary"
            disabled={saving || !label.trim()}
            onClick={handleSave}
          >
            {saving && (
              <Spinner animation="border" size="sm" className="me-1" />
            )}
            Save profile
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
};
